import { Color, Spacing } from '@persona-id/design-tokens';
import { ComponentPropsWithoutRef, FC, KeyboardEventHandler, ReactNode, useRef } from 'react';
import styled from 'styled-components';

const TabBarContainer = styled.div`
  display: flex;
  height: ${Spacing[6]};
  position: relative;
  box-sizing: border-box;
  border-bottom: 2px solid ${Color.BorderDivider};
  justify-content: space-between;
`;

export const StyledTabContainer = styled.div`
  display: flex;
  gap: ${Spacing[4]};
`;

const RightActions = styled.div`
  align-self: center;
`;

interface TabContainerProps extends ComponentPropsWithoutRef<'div'> {
  children: ReactNode;
}

export const TabContainer = ({ children, ...props }: TabContainerProps) => {
  const tabListRef = useRef<HTMLDivElement>(null);

  const getIndexOfCurrentTab = (list: Element[]) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return getIndex(list, document.activeElement);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/tab_role
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const list = getTabList(tabListRef.current.children);

    if (document.activeElement && !list.includes(document.activeElement)) {
      return;
    }

    switch (e.key) {
      case 'ArrowLeft': {
        // Go to previous tab
        const curIndex = getIndexOfCurrentTab(list);
        const nextIndex = curIndex === 0 ? list.length - 1 : curIndex - 1;
        (list[nextIndex] as HTMLElement).focus();
        break;
      }
      case 'ArrowRight': {
        // Go to next tab
        const curIndex = getIndexOfCurrentTab(list);
        (list[(curIndex + 1) % list.length] as HTMLElement).focus();
        break;
      }
      case 'Home': {
        // Go to first tab
        (list[0] as HTMLElement).focus();
        break;
      }
      case 'End': {
        // Go to last tab
        (list[list.length - 1] as HTMLElement).focus();
        break;
      }
      default:
        break;
    }
  };

  return (
    <StyledTabContainer role='tablist' ref={tabListRef} onKeyDown={handleKeyDown} {...props}>
      {children}
    </StyledTabContainer>
  );
};

const getIndex = (children: Element[], element: Element) => {
  return children.indexOf(element);
};

const getTabList = (list: HTMLCollection) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return Array.from(list).filter((e: HTMLElement) => {
    return !(e.getAttribute('aria-disabled') || e.getAttribute('disabled'));
  });
};

export interface TabBarProps extends ComponentPropsWithoutRef<'div'> {
  actions?: ReactNode;
}

const TabBar: FC<TabBarProps> = ({ children, actions, ...elemProps }) => {
  return (
    <TabBarContainer {...elemProps}>
      <TabContainer>{children}</TabContainer>
      {actions && <RightActions>{actions}</RightActions>}
    </TabBarContainer>
  );
};

export default TabBar;
